/**
 * Proof Key for Code Exchange - AUTH-004, RFC 7636.
 *
 * A single-page app cannot keep a client secret, so `auth-stack.ts` registers the
 * web client with `generateSecret: false` and the authorization-code grant. PKCE
 * is what replaces the secret: the app invents a random verifier, sends only its
 * SHA-256 hash to Cognito with the authorize request, and presents the verifier
 * itself when redeeming the code. An attacker who intercepts the code from the
 * redirect cannot exchange it without the verifier, which never left this tab.
 *
 * `S256` and not `plain`. `plain` sends the verifier as the challenge, which is
 * the same as not doing this at all, and Cognito accepts it.
 */

/** RFC 7636 allows 43-128 characters. 32 random bytes base64url-encode to 43. */
const VERIFIER_BYTES = 32;

/**
 * base64url, per RFC 7636 §4.2: base64 with `+/` swapped for `-_` and the
 * padding removed. Plain base64 is not URL-safe and Cognito rejects the request.
 */
function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function createVerifier(
  crypto: Pick<Crypto, 'getRandomValues'> = globalThis.crypto,
): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(VERIFIER_BYTES)));
}

export async function challengeFor(
  verifier: string,
  subtle: Pick<SubtleCrypto, 'digest'> = globalThis.crypto.subtle,
): Promise<string> {
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/**
 * Guards the redirect against a code replayed from somewhere else: the value is
 * sent to Cognito and must come back unchanged on the callback.
 */
export function createState(crypto: Pick<Crypto, 'getRandomValues'> = globalThis.crypto): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(16)));
}
